import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
    Alert,
    AppBar,
    Box,
    Button,
    Container,
    CircularProgress,
    Paper,
    Stack,
    Toolbar,
    Typography,
} from '@mui/material';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import AuthPanel from '../components/auth/AuthPanel';
import { useAuth } from '../context/AuthContext';
import AuthApi from '../utils/api/authApi';
import SiteFooter from '../components/landing/SiteFooter';

const HEADER_HEIGHT = 88;
const CANONICAL_URL = 'https://finradar.brpadvice.ru/vhod/';

function BrandMark({ subtitle = 'Реальная картина бизнеса' }) {
    return (
        <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #0F172A 0%, #1D4ED8 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#FFFFFF',
                    fontWeight: 800,
                }}
            >
                FR
            </Box>
            <Box>
                <Typography sx={{ fontWeight: 800, color: '#0F172A', lineHeight: 1.1 }}>
                    ФинРадар
                </Typography>
                <Typography variant="caption" sx={{ color: '#667085', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    {subtitle}
                </Typography>
            </Box>
        </Stack>
    );
}

function PromisePoint({ text }) {
    return (
        <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
                sx={{
                    width: 26,
                    height: 26,
                    borderRadius: 999,
                    backgroundColor: '#EAF2FF',
                    color: '#155EEF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                <TaskAltRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ color: '#344054', fontWeight: 600, lineHeight: 1.5 }}>
                {text}
            </Typography>
        </Stack>
    );
}

function ProductMockCard() {
    return (
        <Paper
            elevation={0}
            sx={{
                overflow: 'hidden',
                borderRadius: 4,
                border: '1px solid rgba(152, 162, 179, 0.24)',
                backgroundColor: 'rgba(255,255,255,0.72)',
            }}
        >
            <Box sx={{ position: 'relative' }}>
                <Box
                    component="img"
                    src="/assets/images/Актуальное в ФинРадаре.webp"
                    alt="Интерфейс ФинРадара"
                    sx={{
                        display: 'block',
                        width: '100%',
                        height: 'auto',
                        filter: 'grayscale(1)',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(180deg, rgba(248, 250, 252, 0.32) 0%, rgba(148, 163, 184, 0.48) 100%)',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        border: '1px solid rgba(255,255,255,0.28)',
                        pointerEvents: 'none',
                    }}
                />
            </Box>
            <Box
                sx={{
                    px: 2.25,
                    py: 1.75,
                    borderTop: '1px solid rgba(152, 162, 179, 0.18)',
                    backgroundColor: 'rgba(248, 250, 252, 0.86)',
                }}
            >
                <Typography
                    sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: '#475467',
                        lineHeight: 1.6,
                    }}
                >
                    После регистрации создайте проект, загружайте файлы и переходите к рабочему сценарию без лишней подготовки.
                </Typography>
            </Box>
        </Paper>
    );
}

function VhodPage() {
    const [isHydrated, setIsHydrated] = useState(false);
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { login } = useAuth();
    const linkSignInStartedRef = useRef(false);
    const [linkSignInState, setLinkSignInState] = useState({
        status: 'idle',
        message: '',
    });

    const action = searchParams.get('action');
    const linkEmail = String(searchParams.get('email') || '').trim().toLowerCase();
    const linkHash = searchParams.get('k') || '';
    const isSetPasswordMode = action === 'setPassword' && Boolean(linkEmail && linkHash);
    const legacyLinkUi = String(searchParams.get('ui') || '').trim().toLowerCase();
    const legacyLinkKey = String(searchParams.get('key') || '').trim();
    const legacyLinkPartner = String(searchParams.get('s') || '').trim();
    const isLegacySignInByLinkMode = action === 'in';

    useEffect(() => {
        setIsHydrated(true);
    }, []);

    useEffect(() => {
        if (!isLegacySignInByLinkMode) {
            return undefined;
        }

        if (!legacyLinkUi || !legacyLinkKey || !legacyLinkPartner) {
            setLinkSignInState({
                status: 'error',
                message: 'Авторизационная ссылка некорректна. Запросите новую ссылку или войдите по email и паролю.',
            });
            return undefined;
        }

        if (linkSignInStartedRef.current) {
            return undefined;
        }

        linkSignInStartedRef.current = true;
        setLinkSignInState({
            status: 'loading',
            message: 'Проверяем авторизационную ссылку...',
        });

        AuthApi.authSignInByLink({
            action: 'in',
            ui: legacyLinkUi,
            key: legacyLinkKey,
            s: legacyLinkPartner,
        })
            .then(() => {
                setLinkSignInState({
                    status: 'success',
                    message: 'Ссылка подтверждена. Перенаправляем в кабинет...',
                });
                login();
            })
            .catch((error) => {
                setLinkSignInState({
                    status: 'error',
                    message: error?.message || 'Ссылка недействительна или устарела. Запросите новую ссылку или войдите по email и паролю.',
                });
            });

        return undefined;
    }, [isLegacySignInByLinkMode, legacyLinkUi, legacyLinkKey, legacyLinkPartner, login]);

    const handleFooterRegisterClick = () => {
        navigate('/vhod/');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#F5F7FA' }}>
            <Helmet>
                <title>ФинРадар - вход и активация аккаунта</title>
                <meta
                    name="description"
                    content="Вход, активация аккаунта и сброс пароля в ФинРадаре."
                />
                <link rel="canonical" href={CANONICAL_URL} />
            </Helmet>

            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.92)',
                    color: '#101828',
                    borderBottom: '1px solid #EAECF0',
                    backdropFilter: 'blur(16px)',
                }}
            >
                <Container maxWidth="xl">
                    <Toolbar disableGutters sx={{ minHeight: `${HEADER_HEIGHT}px`, gap: 2 }}>
                        <Box
                            component={RouterLink}
                            to="/"
                            sx={{ textDecoration: 'none', flexGrow: 1 }}
                        >
                            <BrandMark />
                        </Box>
                        <Button
                            component={RouterLink}
                            to="/"
                            sx={{
                                display: { xs: 'none', md: 'inline-flex' },
                                color: '#344054',
                                textTransform: 'none',
                                fontWeight: 700,
                            }}
                        >
                            О продукте
                        </Button>
                    </Toolbar>
                </Container>
            </AppBar>

            <Container maxWidth="xl" sx={{ py: { xs: 5, md: 9 } }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', lg: '0.92fr 1.08fr' },
                        gap: { xs: 3, md: 4 },
                        alignItems: 'start',
                    }}
                >
                    <Paper
                        elevation={0}
                        sx={{
                            display: { xs: 'none', lg: 'block' },
                            p: { lg: 4 },
                            borderRadius: 6,
                            border: '1px solid rgba(220, 227, 240, 0.84)',
                            background: 'linear-gradient(180deg, rgba(248, 250, 252, 0.96) 0%, rgba(255, 255, 255, 0.92) 100%)',
                        }}
                    >
                        <Stack spacing={3.5}>
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignSelf: 'flex-start',
                                    px: 1.5,
                                    py: 0.75,
                                    borderRadius: 999,
                                    backgroundColor: '#F2F4F7',
                                    color: '#344054',
                                    fontWeight: 700,
                                }}
                            >
                                Начните сразу после входа
                            </Box>
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: { xs: 34, md: 50 },
                                        lineHeight: 1.04,
                                        fontWeight: 800,
                                        color: '#182230',
                                        fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
                                        maxWidth: 560,
                                    }}
                                >
                                    Один шаг до начала работы
                                </Typography>
                                <Typography sx={{ mt: 2, maxWidth: 560, color: '#667085', lineHeight: 1.7, fontSize: 17 }}>
                                    Создайте проект, добавьте выписки и получите понятную основу для работы: контрагенты, расчёты, сделки, операции и зоны внимания.
                                </Typography>
                            </Box>
                            <Stack spacing={1.5}>
                                <PromisePoint text="7 дней бесплатно" />
                                <PromisePoint text="Начните работу сразу после регистрации" />
                                <PromisePoint text="Создание первого проекта в 2 клика" />
                            </Stack>
                            <ProductMockCard />
                        </Stack>
                    </Paper>

                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 3, md: 4 },
                            borderRadius: 6,
                            border: '1px solid rgba(25, 118, 210, 0.14)',
                            color: '#16324f',
                            background: 'linear-gradient(135deg, #f4f8ff 0%, #eef7f1 55%, #fff8ea 100%)',
                            alignSelf: 'start',
                            boxShadow: {
                                xs: '0 24px 48px rgba(15, 23, 42, 0.10)',
                                md: '0 32px 72px rgba(15, 23, 42, 0.14)',
                            },
                        }}
                    >
                        {!isHydrated && !isLegacySignInByLinkMode ? (
                            <Stack spacing={2.5} alignItems="flex-start">
                                <Typography component="h1" sx={{ fontSize: 28, fontWeight: 800, color: '#182230' }}>
                                    Загружаем форму входа
                                </Typography>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <CircularProgress size={22} />
                                    <Typography sx={{ color: '#667085', lineHeight: 1.7 }}>
                                        Подготавливаем безопасное подключение...
                                    </Typography>
                                </Stack>
                            </Stack>
                        ) : isLegacySignInByLinkMode ? (
                            <Stack spacing={2.5}>
                                <Typography component="h1" sx={{ fontSize: 28, fontWeight: 800, color: '#182230' }}>
                                    Вход по ссылке
                                </Typography>
                                <Alert
                                    severity={linkSignInState.status === 'error' ? 'error' : 'info'}
                                    icon={linkSignInState.status === 'loading' ? <CircularProgress size={18} /> : undefined}
                                >
                                    {linkSignInState.message || 'Проверяем авторизационную ссылку...'}
                                </Alert>
                                {linkSignInState.status === 'error' ? (
                                    <Button
                                        variant="contained"
                                        onClick={() => navigate(location.pathname, { replace: true })}
                                        sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700 }}
                                    >
                                        Войти по email и паролю
                                    </Button>
                                ) : null}
                            </Stack>
                        ) : (
                            <AuthPanel
                                initialMode="signup"
                                showRecoveryTab
                                setPasswordFlow={{
                                    enabled: isSetPasswordMode,
                                    email: linkEmail,
                                    k: linkHash,
                                }}
                                onClearSetPasswordFlow={() => navigate(location.pathname, { replace: true })}
                                onAuthenticated={() => login()}
                                copy={{
                                    loginDescription: 'Войдите по email и паролю.',
                                    signupSuccessAlert: 'Письмо для активации отправлено, его доставка может занять до 2 минут. Перейдите по ссылке из email и создайте пароль.',
                                }}
                            />
                        )}
                    </Paper>
                </Box>
            </Container>

            <SiteFooter onRegisterClick={handleFooterRegisterClick} />
        </Box>
    );
}

export default VhodPage;
